<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { getStaticPickupShippingOffer } from '@/api/shippingCalculator';
import Checkbox from '@/components/base/form/Checkbox.vue';
import GlobalErrorNotification from '@/components/base/GlobalErrorNotification.vue';
import Notification from '@/components/base/Notification.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import WindowObserver from '@/components/base/WindowObserver.vue';
import DeliveryDate from '@/components/checkout/steps/delivery/DeliveryDate.vue';
import ShipmentDelivery from '@/components/checkout/steps/delivery/ShipmentDelivery.vue';
import Skeleton from '@/components/checkout/steps/delivery/Skeleton.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { CalculateShippingOffersOptions, useDelivery } from '@/composables/useDelivery';
import { useDiscount } from '@/composables/useDiscount';
import { containsGiftPurchase } from '@/lib/cart/lineItem';
import { hasOnlyGiftCertificate, isGreetingCardLineItem } from '@/utils/cart';
import { DateString } from '@/utils/dateTime';
import { reportError } from '@/utils/reportError';

const { delivery, skipLoadingOffersOnMount } = defineProps<{
  delivery: ReturnType<typeof useDelivery>;
  skipLoadingOffersOnMount?: boolean;
}>();
const emit = defineEmits<{ 'step-complete': [] }>();

const router = useRouter();

const isLoadingShippingOffers = ref(true);
const dashlinkAllowed = defineModel<boolean>('dashlinkAllowed');
const lasershipAllowed = defineModel<boolean>('lasershipAllowed');
const ontracAllowed = defineModel<boolean>('ontracAllowed');
const regionalCarriersAllowed = defineModel<boolean>('regionalCarriersAllowed');

const selectedShipDate = ref<DateString>();
const store = useStore();

const { createdFromOrderUpload, lineItems, loadCart } = useCart(store);
const { cxMode } = useCheckout(store);
const { appliedDiscountCodes } = useDiscount(store);

const {
  calculateShippingOffers,
  futureDeliveryDates,
  getShipDates,
  isPickup,
  requestedDeliveryDate,
  saveDeliveryOptions,
  setRequestedShipDate,
  setShippingOfferSets,
  shippingOfferSets,
  standardShipments,
} = delivery;

const initiallyLoading = computed(
  () => isLoadingShippingOffers.value && shippingOfferSets.value.length === 0,
);
const deliveryErrorElement = ref<HTMLElement>();
const deliveryErrorIntersection = ref<IntersectionObserverEntry>();

const firstAppliedDiscountCode = computed(() => appliedDiscountCodes.value[0]?.code);

getShipDates(standardShipments.value?.map((s) => s.address.country) ?? ['US']);

const intersectDeliveryError = (intersectionObserver: IntersectionObserverEntry) => {
  deliveryErrorIntersection.value = intersectionObserver;
};

const exitDeliveryError = () => {
  deliveryErrorIntersection.value = undefined;
};

const containsGift = (shipmentKey: string) => {
  const shipment = standardShipments.value.find((ship) => ship.key === shipmentKey);
  return containsGiftPurchase(shipment?.lineItems ?? []);
};

const handleSaveDeliveryOptions = useCallback(saveDeliveryOptions);

const shippingCalculatorFailure = ref('');
const loadShippingOffers = async (options?: CalculateShippingOffersOptions) => {
  try {
    shippingCalculatorFailure.value = '';

    if (isPickup.value) {
      const shipment = standardShipments.value[0];
      const pickupDate = shipment.customFields?.earliestArrivalOn;
      const pickupShippingOffer = getStaticPickupShippingOffer(pickupDate);
      setShippingOfferSets([
        {
          address: shipment.address,
          key: shipment.key,
          shippingOffers: [pickupShippingOffer],
        },
      ]);
    } else {
      const { offerSets, futureDeliveryDates: deliveryDates } = await calculateShippingOffers({
        ...options,
        cxMode: cxMode.value,
        regionalCarriersAllowed: regionalCarriersAllowed.value,
        dashlinkAllowed: dashlinkAllowed.value,
        ontracAllowed: ontracAllowed.value,
        lasershipAllowed: lasershipAllowed.value,
      });
      futureDeliveryDates.value = deliveryDates;
      setShippingOfferSets(offerSets);
    }

    handleSaveDeliveryOptions.execute();
  } catch (error) {
    shippingCalculatorFailure.value =
      error instanceof Error ? error.message : 'Could not find any shipping offers';

    reportError(error, 'Failed to initialize Delivery step');
  }
};

const onShippingOfferChanged = async (shippingOfferSelected: boolean) => {
  if (shippingOfferSelected) handleSaveDeliveryOptions.execute();
};

const showRegionalCarriers = computed(
  () => cxMode.value && !isPickup.value && !hasOnlyGiftCertificate(lineItems.value),
);

const updateShippingOffers = async (options?: CalculateShippingOffersOptions) => {
  isLoadingShippingOffers.value = true;
  await loadShippingOffers(options);
  isLoadingShippingOffers.value = false;
};

watch(
  [
    dashlinkAllowed,
    ontracAllowed,
    lasershipAllowed,
    regionalCarriersAllowed,
    firstAppliedDiscountCode,
  ],
  () => {
    updateShippingOffers();
  },
);

watch(selectedShipDate, async (newShipDate) => {
  setRequestedShipDate(newShipDate as DateString);
  await updateShippingOffers();
});

watch(requestedDeliveryDate, async () => {
  await updateShippingOffers();
});

const submit = async () => {
  await handleSaveDeliveryOptions.execute();
  if (!handleSaveDeliveryOptions.error) {
    emit('step-complete');
  }
};

onMounted(async () => {
  if (!skipLoadingOffersOnMount) {
    await loadShippingOffers();
  }
  isLoadingShippingOffers.value = false;
});

watch(
  () => handleSaveDeliveryOptions.error,
  (error) => {
    if (error) {
      if ((error as Error & { status?: string }).status === 'newUnassignedGiftLineItem') {
        router.push('/checkout/address/multi');
        loadCart(true);
        return;
      }
      if (!deliveryErrorIntersection.value) {
        deliveryErrorElement.value?.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }
  },
);
</script>

<template>
  <div
    class="transition duration-200 ease-in-out delay-150"
    :class="{
      'opacity-50 pointer-events-none':
        isLoadingShippingOffers || handleSaveDeliveryOptions.isPending,
    }"
  >
    <WindowObserver @intersect="intersectDeliveryError" @exit-view="exitDeliveryError">
      <div ref="deliveryErrorElement">
        <GlobalErrorNotification v-if="handleSaveDeliveryOptions.error" class="mt-4 md:mt-8" />
      </div>
    </WindowObserver>
    <transition name="fade">
      <Notification v-if="shippingCalculatorFailure" class="mt-3 md:mt-6" theme="error">
        {{ shippingCalculatorFailure }}
      </Notification>
    </transition>
    <template v-if="shippingOfferSets.filter((set) => set.selectedShippingOffer).length > 1">
      <DeliveryDate
        v-model="requestedDeliveryDate"
        class="mt-3 md:mt-6"
        :futureDeliveryDates
        :loadingShippingOffers="isLoadingShippingOffers || false"
        @get-future-delivery="updateShippingOffers({ deliveryDateCalendar: true })"
      />
    </template>
    <div v-for="(shipment, i) in shippingOfferSets" :key="shipment.key" class="mt-3 md:mt-6">
      <div v-if="shippingOfferSets.length > 1" class="font-semibold">
        Address {{ shipment.multishipSerial }}:
        {{ shipment.address.name }}
        {{ shipment.address.city ? ` in ${shipment.address.city}` : '' }}
      </div>
      <ShipmentDelivery
        v-model:addHeatResistantPackaging="shippingOfferSets[i].addHeatResistantPackaging"
        v-model:giftOptions="shippingOfferSets[i].giftOptions"
        v-model:selectedShippingOffer="shippingOfferSets[i].selectedShippingOffer"
        :addressKey="shipment.key"
        :containsGift="containsGift(shipment.key)"
        :offerPackingSlipMessage="!lineItems.some(isGreetingCardLineItem)"
        :selectedDeliveryDate="requestedDeliveryDate"
        :shippingOffers="shippingOfferSets[i].shippingOffers"
        @selected-delivery-option="onShippingOfferChanged"
      >
        <template
          v-if="
            shippingOfferSets.filter((set) => set.selectedShippingOffer).length === 1 && !isPickup
          "
        >
          <DeliveryDate
            v-model="requestedDeliveryDate"
            class="mt-3 md:mt-6"
            :futureDeliveryDates
            :loadingShippingOffers="isLoadingShippingOffers || false"
            @get-future-delivery="updateShippingOffers({ deliveryDateCalendar: true })"
          />
        </template>
        <div v-if="showRegionalCarriers" class="flex mt-3 text-base md:mt-6">
          <div class="flex mr-6">
            <Checkbox
              v-model="regionalCarriersAllowed"
              class="font-normal"
              data-test="regional-carriers-checkbox"
              :disabled="createdFromOrderUpload"
            >
              Regional carriers
            </Checkbox>
          </div>
          <div class="flex mr-6">
            <Checkbox
              v-model="dashlinkAllowed"
              class="font-normal"
              data-test="dashlink-checkbox"
              :disabled="createdFromOrderUpload"
            >
              DashLink
            </Checkbox>
          </div>
          <div class="flex mr-6">
            <Checkbox
              v-model="ontracAllowed"
              class="font-normal"
              data-test="ontrac-checkbox"
              :disabled="createdFromOrderUpload"
            >
              Ontrac
            </Checkbox>
          </div>
          <div class="flex">
            <Checkbox
              v-model="lasershipAllowed"
              class="font-normal"
              data-test="lasership-checkbox"
              :disabled="createdFromOrderUpload"
            >
              Lasership
            </Checkbox>
          </div>
        </div>
      </ShipmentDelivery>
    </div>
    <Skeleton v-if="initiallyLoading" class="pt-4" />
    <ThemedButton
      v-else
      class="mt-4 md:mt-8"
      data-test="shipping-continue"
      theme="gray"
      :isLoading="handleSaveDeliveryOptions.isPending"
      :disabled="!!shippingCalculatorFailure"
      @click="submit"
    >
      Continue
    </ThemedButton>
  </div>
</template>
